/**
 * HR — ติดตามสัญญาจ้าง (เฟส 1 ของ requirement HR ต.ค. 2026).
 *
 * แสดง milestone สัญญาของพนักงานทุกคน (ครบ 1 เดือน / 3 เดือน / ทุกปี) คำนวณจากวันเริ่มงาน
 * HR เห็นว่าใครถึงกำหนด · เซ็นแล้ว/ยัง · เกินกำหนดไหม แล้วกดทำเครื่องหมายเซ็นได้
 * (แจ้งเตือนล่วงหน้า 7/3/1 วัน ส่งผ่าน LINE อัตโนมัติโดย worker cron)
 */
import { useMemo, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useContracts, useUpdateContract, type ContractMilestone } from '../../hooks';
import { useToast } from '../../components/ui/toast';
import { CheckCircle2, Clock, AlertTriangle, RotateCcw, FileText } from 'lucide-react';

type Filter = 'due' | 'pending' | 'signed' | 'all';

const DAY_MS = 86_400_000;
function daysLeft(due: string): number {
  const today = new Date(new Date().toISOString().slice(0, 10) + 'T00:00:00Z').getTime();
  return Math.round((new Date(due + 'T00:00:00Z').getTime() - today) / DAY_MS);
}

export function HRContracts() {
  const { data, isLoading } = useContracts();
  const update = useUpdateContract();
  const toast = useToast();
  const [filter, setFilter] = useState<Filter>('due');

  const rows = useMemo(() => data?.contracts ?? [], [data]);

  const counts = useMemo(() => {
    let due = 0, pending = 0, signed = 0;
    for (const r of rows) {
      if (r.status === 'signed') signed++;
      else if (r.status === 'pending') {
        pending++;
        if (daysLeft(r.due_date) <= 7) due++;
      }
    }
    return { due, pending, signed, all: rows.length };
  }, [rows]);

  const filtered = useMemo(() => {
    const list = rows.filter((r) => {
      if (filter === 'all') return true;
      if (filter === 'signed') return r.status === 'signed';
      if (filter === 'pending') return r.status === 'pending';
      // 'due' = pending ที่ใกล้ครบ (≤7 วัน) หรือเกินกำหนดแล้ว
      return r.status === 'pending' && daysLeft(r.due_date) <= 7;
    });
    // เรียง: เกินกำหนด/ใกล้สุดก่อน
    return [...list].sort((a, b) => a.due_date.localeCompare(b.due_date));
  }, [rows, filter]);

  async function setStatus(r: ContractMilestone, status: 'signed' | 'pending') {
    try {
      await update.mutateAsync({ id: r.id, status });
      toast.success(status === 'signed' ? 'ทำเครื่องหมายเซ็นแล้ว' : 'เปิดรายการกลับเป็นรอเซ็น');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  const tabs: { key: Filter; label: string; n: number }[] = [
    { key: 'due', label: 'ใกล้ครบ/เกินกำหนด', n: counts.due },
    { key: 'pending', label: 'รอเซ็นทั้งหมด', n: counts.pending },
    { key: 'signed', label: 'เซ็นแล้ว', n: counts.signed },
    { key: 'all', label: 'ทั้งหมด', n: counts.all },
  ];

  return (
    <AdminLayout title="ติดตามสัญญาจ้าง">
      <div className="flex flex-wrap gap-2 mb-4">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setFilter(t.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition ${
              filter === t.key
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
            }`}
          >
            {t.label} <span className="opacity-70">({t.n})</span>
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-slate-400 text-sm py-10 text-center">กำลังโหลด…</div>
      ) : filtered.length === 0 ? (
        <div className="text-slate-400 text-sm py-10 text-center flex flex-col items-center gap-2">
          <FileText className="w-8 h-8 opacity-40" />
          ไม่มีรายการในหมวดนี้
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="p-3 font-medium">พนักงาน</th>
                <th className="p-3 font-medium">รายการสัญญา</th>
                <th className="p-3 font-medium whitespace-nowrap">ครบกำหนด</th>
                <th className="p-3 font-medium">สถานะ</th>
                <th className="p-3 font-medium text-right">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const dl = daysLeft(r.due_date);
                const overdue = r.status === 'pending' && dl < 0;
                const soon = r.status === 'pending' && dl >= 0 && dl <= 7;
                return (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td className="p-3">
                      <div className="font-medium text-slate-800">
                        {r.employee_name}{r.nickname ? ` (${r.nickname})` : ''}
                      </div>
                      <div className="text-xs text-slate-400">{r.emp_code} · {r.department || '—'}</div>
                    </td>
                    <td className="p-3 text-slate-700">{r.milestone_label}</td>
                    <td className="p-3 whitespace-nowrap">
                      <div className="text-slate-700">{r.due_date}</div>
                      {r.status === 'pending' && (
                        <div className={`text-xs ${overdue ? 'text-red-600' : soon ? 'text-amber-600' : 'text-slate-400'}`}>
                          {overdue ? `เกิน ${-dl} วัน` : dl === 0 ? 'ครบวันนี้' : `อีก ${dl} วัน`}
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      {r.status === 'signed' ? (
                        <span className="inline-flex items-center gap-1 text-green-700 bg-green-50 px-2 py-0.5 rounded-full text-xs font-medium">
                          <CheckCircle2 className="w-3.5 h-3.5" /> เซ็นแล้ว
                        </span>
                      ) : overdue ? (
                        <span className="inline-flex items-center gap-1 text-red-700 bg-red-50 px-2 py-0.5 rounded-full text-xs font-medium">
                          <AlertTriangle className="w-3.5 h-3.5" /> เกินกำหนด
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full text-xs font-medium">
                          <Clock className="w-3.5 h-3.5" /> รอเซ็น
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      {r.status === 'signed' ? (
                        <button
                          onClick={() => setStatus(r, 'pending')}
                          disabled={update.isPending}
                          className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-700 text-xs"
                        >
                          <RotateCcw className="w-3.5 h-3.5" /> เปิดกลับ
                        </button>
                      ) : (
                        <button
                          onClick={() => setStatus(r, 'signed')}
                          disabled={update.isPending}
                          className="inline-flex items-center gap-1 bg-slate-900 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-slate-700 disabled:opacity-50"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" /> ทำเครื่องหมายเซ็นแล้ว
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
